<template>
  <div>
    <ul
      id="addendum-accordion"
      class="accordion"
      data-accordion
      data-deep-link="true"
      data-allow-all-closed="true"
      data-multi-expand="true"
      data-update-history="true"
      data-deep-link-smudge="500"
    >
      <li
        v-for="(revision, index) in addendums"
        :key="revision.commentUpdatedAt"
        class="accordion-item"
        :class="{'is-active': index === 0}"
        data-accordion-item
        style="position: relative;"
      >
        <div class="item__header">
          <div class="header__title">
            <span class="title__left">
              <p class="title__text">{{ $t('summary.location.addendum') }} {{ revision.version }}</p>
              <i
                v-if="isPicView"
                class="fa fa-pencil fa-lg icon accordion-title-edit-icon"
                @click="editComment(index)"
              />
              <p
                v-if="showEditHistory(index)"
                class="edit-history"
              >
                {{ lastUpdatedContent(index) }}
              </p>
            </span>
            <span
              :href="'#addendum-accordion-' + index"
              :class="chevronStatus(index)"
              @click="toggleItem(index)"
              v-html="chevronHtmlCode"
            />
          </div>
          <p
            v-if="commentsToDisplay[index] && !showEditArea(index)"
            :class="commentStatus(index)"
          >
            {{ commentsToDisplay[index] }}
          </p>
          <textarea
            v-if="showEditArea(index)"
            :ref="'edit-comment-area-' + index"
            v-model="commentsToDisplay[index]"
            rows="3"
            :placeholder="$t('summary.addendumComment')"
            @blur="saveComment"
          />
        </div>
        <div
          :id="'addendum-accordion-' + index"
          class="accordion-content"
          data-tab-content
        >
          <AddendumItem :addendums="revision.addendum" />
        </div>
      </li>
    </ul>
    <VendorBidStatusCollapse
      v-if="isInAddendum"
    />
  </div>
</template>

<script>
import moment from 'moment'
import { mapActions, mapGetters } from 'vuex'

import AddendumItem from '@/modules/tenders/review/AddendumItem'
import termsOfConditionsMixin from '@/modules/tenders/termsOfConditionsMixin'
import { DD_MMM_YY_HH_MM as dateFormat } from '@/utils/dateUtil'
import { diffAllVersions } from '@/api/tender-addendums/request'
import VendorBidStatusCollapse from '../view/VendorBidStatusCollapse'
import { isTenderInAddendum } from '@/api/tenders/request'

export default {
  name: 'ReviewAddendum',
  components: {
    AddendumItem,
    VendorBidStatusCollapse,
  },
  mixins: [termsOfConditionsMixin],
  props: {
    tenderId: {
      type: String,
      default: '',
    },
    isBid: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    const commentRelatedContent = {
      title: this.$i18n.t('tender.comment.title'),
      confirmBtn: this.$i18n.t('tender.comment.confirmBtn'),
      cancelBtn: this.$i18n.t('tender.comment.cancelBtn'),
    }

    return {
      revisions: [],
      addendums: [],
      comments: [],
      commentsToDisplay: [],
      currentCommentIndex: -1,
      commentEditors: [],
      commentUpdateTimes: [],
      commentRelatedContent,
      isExpand: [true, false, false],
      chevronHtmlCode: '&#8249;',
    }
  },
  computed: {
    ...mapGetters('tenders', ['currentTender', 'isPicView', 'tenderDepartment']),
    isInAddendum () {
      return isTenderInAddendum(this.currentTender.general, true)
    },
  },
  watch: {
    async tenderId (val) {
      if (val) {
        await this.refreshPage()
        setTimeout(this.initAccordion, 0)
      }
    },
  },
  destroyed () {
    if (this.accordion) {
      this.accordion.destroy()
    }
  },
  async mounted () {
    await this.loadTenderDepartment(this.$router.currentRoute.params.id, this.isBid)
    await this.refreshPage()
    setTimeout(this.initAccordion, 0)
  },
  methods: {
    ...mapActions('tenders', [
      'updateAddendumComment',
    ]),
    timeout (ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    initAccordion () {
      if (this.accordion) {
        this.accordion.destroy()
      }
      if (!this.accordion) {
        this.accordion = new Foundation.Accordion($('#addendum-accordion'))
      }
    },
    async refreshPage () {
      this.addendums = await diffAllVersions(this.tenderId || this.$router.currentRoute.params.id)
      this.commentsToDisplay = this.addendums.map((addendum) => addendum.comment)
      this.commentUpdateTimes = this.addendums.map((addendum) => addendum.commentUpdatedAt)
      this.commentEditors = this.addendums.map((addendum) => addendum.commentUpdatedBy)
    },
    async editComment (index) {
      this.currentCommentIndex = index
      await this.$nextTick()
      this.$refs[`edit-comment-area-${index}`][0].focus()
    },
    async saveComment () {
      await this.updateAddendumComment({
        tenderId: this.$router.currentRoute.params.id,
        commentIndex: this.addendums.length - this.currentCommentIndex - 1,
        content: this.commentsToDisplay[this.currentCommentIndex],
      })
      this.currentCommentIndex = -1
      this.refreshPage()
    },
    cancelComment () {
      this.currentCommentIndex = -1
    },
    toggleItem (index) {
      $('#addendum-accordion').foundation('toggle', $(`#addendum-accordion-${index}`))
      this.isExpand.splice(index, 1, !this.isExpand[index])
    },
    lastUpdatedContent (index) {
      const updateTime = moment(this.commentUpdateTimes[index]).format(dateFormat)
      return this.$i18n.t('lastUpdatedBy', [this.commentEditors[index], updateTime])
    },
    showEditHistory (index) {
      return this.commentUpdateTimes[index] && this.commentEditors[index]
    },
    showEditArea (index) {
      return this.currentCommentIndex === index
    },
    chevronStatus (index) {
      return this.isExpand[index] ? 'accordion-title-chevron accordion-title-chevron-open' : 'accordion-title-chevron'
    },
    commentStatus (index) {
      return this.isExpand[index] ? 'comment' : 'comment comment-hide'
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/accordion';
.accordion-content {
  padding: 0 20px;
}
.dialog-btn {
  width: 100px;
  height: 20px;
  background-color: black;
}
.comment {
  font-size: 14px;
  margin: 0;
  padding: 0;
  margin-right: 40px;
  word-break: break-all;
  white-space: pre-wrap;
}
.comment-hide {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.comment-btns {
  margin-top: 10px;
}
.item__header {
  padding: 14px 24px;
  border-bottom: 1px solid #e6e6e6;

  .header__title {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .title__left {
      display: flex;
      align-items: center;

      p {
        margin: 0;
      }

      .title__text {
        margin: 0;
        font-size: 22px;
        color: #0c3a58;
      }

      .edit-history {
        font-style: italic;
        margin: 0;
        margin-top: 10px;
        color: #a3a3a3;
        font-size: 12px;
        margin-left: 20px;
      }
    }
  }
}
.accordion-title-chevron {
  cursor: pointer;
  font-style: normal;
  font-size: 50px;
  color: #27698D;
  font-family: Verdana;
  transform: rotate(-90deg);
  height: 25px;
  margin-right: 30px;
}
.accordion-title-chevron-open {
  transform: rotate(90deg);
  margin-right: -32px;
}
.accordion-title-edit-icon {
  font-size: 20px;
  margin-left: 16px;
}
.accordion textarea {
  font-size: 14px
}
</style>
